import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PackingList,
  PackingListDetail,
  ShipToLocation,
  Product,
  SearchPackingListsRequest,
  SearchPackingListsResponse,
  CreatePackingListRequest,
  PackingListReportData,
} from '../../services/packingListApi';

interface PackingListState {
  // Packing Lists
  packingLists: PackingList[];
  selectedPackingList: PackingList | null;
  loading: boolean;
  error: string | null;

  // Pagination
  currentPage: number;
  pageSize: number;
  totalCount: number;

  // Filters
  searchTerm: string;
  activeFilter: boolean | null;

  // Dropdown data
  shipToLocations: ShipToLocation[];
  products: Product[];
  loadingDropdowns: boolean;

  // Packing List Details
  selectedDetails: PackingListDetail[];
  detailsLoading: boolean;
  selectedDetailId: string | null;

  // UI State
  showDetailsPanel: boolean;
  showShippingPanel: boolean;
  showExportPanel: boolean;
  editingDetail: PackingListDetail | null;
  
  // Report data
  reportData: PackingListReportData | null;
  reportLoading: boolean;

  // Validation
  validationErrors: string[];
  
  // Totals calculation
  calculatedTotals: {
    totalQuantity: number;
    totalValue: number;
    itemCount: number;
  };
}

const initialState: PackingListState = {
  packingLists: [],
  selectedPackingList: null,
  loading: false,
  error: null,

  currentPage: 1,
  pageSize: 25,
  totalCount: 0,

  searchTerm: '',
  activeFilter: null,

  shipToLocations: [],
  products: [],
  loadingDropdowns: false,

  selectedDetails: [],
  detailsLoading: false,
  selectedDetailId: null,

  showDetailsPanel: false,
  showShippingPanel: true,
  showExportPanel: false,
  editingDetail: null,

  reportData: null,
  reportLoading: false,

  validationErrors: [],

  calculatedTotals: {
    totalQuantity: 0,
    totalValue: 0,
    itemCount: 0,
  },
};

const packingListSlice = createSlice({
  name: 'packingList',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setDropdownsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingDropdowns = action.payload;
    },

    setDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.detailsLoading = action.payload;
    },

    setReportLoading: (state, action: PayloadAction<boolean>) => {
      state.reportLoading = action.payload;
    },

    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },

    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },

    // Packing Lists CRUD
    fetchPackingListsStart: (state, action: PayloadAction<SearchPackingListsRequest | undefined>) => {
      state.loading = true;
      state.error = null;
    },

    fetchPackingListsSuccess: (state, action: PayloadAction<SearchPackingListsResponse>) => {
      state.loading = false;
      state.packingLists = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.currentPage = action.payload.pageNum;
      state.pageSize = action.payload.pageSize;
    },

    fetchPackingListsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Single Packing List
    fetchPackingListByIdStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    fetchPackingListByIdSuccess: (state, action: PayloadAction<PackingList>) => {
      state.loading = false;
      state.selectedPackingList = action.payload;
    },

    fetchPackingListByIdFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create Packing List
    createPackingListStart: (state, action: PayloadAction<CreatePackingListRequest>) => {
      state.loading = true;
      state.error = null;
      state.validationErrors = [];
    },

    createPackingListSuccess: (state, action: PayloadAction<PackingList>) => {
      state.loading = false;
      state.packingLists.unshift(action.payload);
      state.totalCount += 1;
      state.selectedPackingList = action.payload;
    },

    createPackingListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update Packing List
    updatePackingListStart: (state, action: PayloadAction<Partial<PackingList>>) => {
      state.loading = true;
      state.error = null;
      state.validationErrors = [];
    },

    updatePackingListSuccess: (state, action: PayloadAction<PackingList>) => {
      state.loading = false;
      const index = state.packingLists.findIndex(pl => pl.id === action.payload.id);
      if (index !== -1) {
        state.packingLists[index] = action.payload;
      }
      if (state.selectedPackingList?.id === action.payload.id) {
        state.selectedPackingList = action.payload;
      }
    },

    updatePackingListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete Packing List
    deletePackingListStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    deletePackingListSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.packingLists = state.packingLists.filter(pl => pl.id !== action.payload);
      state.totalCount -= 1;
      if (state.selectedPackingList?.id === action.payload) {
        state.selectedPackingList = null;
      }
    },

    deletePackingListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Dropdown data
    fetchDropdownsStart: (state) => {
      state.loadingDropdowns = true;
    },

    fetchDropdownsSuccess: (state, action: PayloadAction<{
      shipToLocations: ShipToLocation[];
      products: Product[];
    }>) => {
      state.loadingDropdowns = false;
      state.shipToLocations = action.payload.shipToLocations;
      state.products = action.payload.products;
    },

    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.loadingDropdowns = false;
      state.error = action.payload;
    },

    // Packing List Details
    fetchDetailsStart: (state, action: PayloadAction<string>) => {
      state.detailsLoading = true;
    },

    fetchDetailsSuccess: (state, action: PayloadAction<PackingListDetail[]>) => {
      state.detailsLoading = false;
      state.selectedDetails = action.payload;
      // Calculate totals
      state.calculatedTotals = action.payload.reduce(
        (totals, detail) => ({
          totalQuantity: totals.totalQuantity + detail.Quantity,
          totalValue: totals.totalValue + (detail.TotalPrice || 0),
          itemCount: totals.itemCount + 1,
        }),
        { totalQuantity: 0, totalValue: 0, itemCount: 0 }
      );
    },

    fetchDetailsFailure: (state, action: PayloadAction<string>) => {
      state.detailsLoading = false;
      state.error = action.payload;
    },

    // Add Detail
    addDetailStart: (state, action: PayloadAction<{ packingListId: string; detail: Omit<PackingListDetail, 'id' | 'PackingListId'> }>) => {
      // Optimistic update can be added here
    },

    addDetailSuccess: (state, action: PayloadAction<PackingListDetail>) => {
      state.selectedDetails.push(action.payload);
      // Recalculate totals
      state.calculatedTotals = state.selectedDetails.reduce(
        (totals, detail) => ({
          totalQuantity: totals.totalQuantity + detail.Quantity,
          totalValue: totals.totalValue + (detail.TotalPrice || 0),
          itemCount: totals.itemCount + 1,
        }),
        { totalQuantity: 0, totalValue: 0, itemCount: 0 }
      );
    },

    addDetailFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Update Detail
    updateDetailStart: (state, action: PayloadAction<PackingListDetail>) => {
      const index = state.selectedDetails.findIndex(detail => detail.id === action.payload.id);
      if (index !== -1) {
        state.selectedDetails[index] = action.payload;
      }
    },

    updateDetailSuccess: (state, action: PayloadAction<PackingListDetail>) => {
      const index = state.selectedDetails.findIndex(detail => detail.id === action.payload.id);
      if (index !== -1) {
        state.selectedDetails[index] = action.payload;
      }
      // Recalculate totals
      state.calculatedTotals = state.selectedDetails.reduce(
        (totals, detail) => ({
          totalQuantity: totals.totalQuantity + detail.Quantity,
          totalValue: totals.totalValue + (detail.TotalPrice || 0),
          itemCount: totals.itemCount + 1,
        }),
        { totalQuantity: 0, totalValue: 0, itemCount: 0 }
      );
    },

    updateDetailFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Delete Detail
    deleteDetailStart: (state, action: PayloadAction<string>) => {
      state.selectedDetails = state.selectedDetails.filter(detail => detail.id !== action.payload);
    },

    deleteDetailSuccess: (state, action: PayloadAction<string>) => {
      state.selectedDetails = state.selectedDetails.filter(detail => detail.id !== action.payload);
      // Recalculate totals
      state.calculatedTotals = state.selectedDetails.reduce(
        (totals, detail) => ({
          totalQuantity: totals.totalQuantity + detail.Quantity,
          totalValue: totals.totalValue + (detail.TotalPrice || 0),
          itemCount: totals.itemCount + 1,
        }),
        { totalQuantity: 0, totalValue: 0, itemCount: 0 }
      );
    },

    deleteDetailFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Report Generation
    generateReportStart: (state, action: PayloadAction<string>) => {
      state.reportLoading = true;
      state.reportData = null;
    },

    generateReportSuccess: (state, action: PayloadAction<PackingListReportData>) => {
      state.reportLoading = false;
      state.reportData = action.payload;
    },

    generateReportFailure: (state, action: PayloadAction<string>) => {
      state.reportLoading = false;
      state.error = action.payload;
    },

    // Filters and search
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page when searching
    },

    setActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.activeFilter = action.payload;
      state.currentPage = 1;
    },

    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },

    // UI State
    toggleDetailsPanel: (state) => {
      state.showDetailsPanel = !state.showDetailsPanel;
    },

    setDetailsPanel: (state, action: PayloadAction<boolean>) => {
      state.showDetailsPanel = action.payload;
    },

    toggleShippingPanel: (state) => {
      state.showShippingPanel = !state.showShippingPanel;
    },

    setShippingPanel: (state, action: PayloadAction<boolean>) => {
      state.showShippingPanel = action.payload;
    },

    toggleExportPanel: (state) => {
      state.showExportPanel = !state.showExportPanel;
    },

    setExportPanel: (state, action: PayloadAction<boolean>) => {
      state.showExportPanel = action.payload;
    },

    // Detail Editing
    setEditingDetail: (state, action: PayloadAction<PackingListDetail | null>) => {
      state.editingDetail = action.payload;
    },

    setSelectedDetailId: (state, action: PayloadAction<string | null>) => {
      state.selectedDetailId = action.payload;
    },

    // Auto-populate shipping info from location
    populateShippingFromLocation: (state, action: PayloadAction<{ field: 'shipFrom' | 'shipTo' | 'billTo'; locationId: string }>) => {
      const location = state.shipToLocations.find(loc => loc.id === action.payload.locationId);
      if (!location || !state.selectedPackingList) return;

      const { field } = action.payload;
      
      if (field === 'shipFrom') {
        state.selectedPackingList.ShipFrom = location.AttName;
        state.selectedPackingList.ShipFromCo = location.AttName;
        state.selectedPackingList.ShipFromSt = location.AttSt;
        state.selectedPackingList.ShipFromCity = location.AttCity;
        state.selectedPackingList.ShipFromState = location.AttState;
        state.selectedPackingList.ShipFromZIP = location.AttZIP;
        state.selectedPackingList.ShipFromPhone = location.AttPhone;
        state.selectedPackingList.ShipFromEmail = location.AttEmail;
      } else if (field === 'shipTo') {
        state.selectedPackingList.ShipTo = location.AttName;
        state.selectedPackingList.ShipToCo = location.AttName;
        state.selectedPackingList.ShipToSt = location.AttSt;
        state.selectedPackingList.ShipToCity = location.AttCity;
        state.selectedPackingList.ShipToState = location.AttState || '';
        state.selectedPackingList.ShipToZIP = location.AttZIP || '';
        state.selectedPackingList.ShipToPhone = location.AttPhone || '';
        state.selectedPackingList.ShipToEmail = location.AttEmail || '';
      } else if (field === 'billTo') {
        state.selectedPackingList.BillTo = location.AttName;
        state.selectedPackingList.BillToCo = location.AttName;
        state.selectedPackingList.BillToSt = location.AttSt;
        state.selectedPackingList.BillToCity = location.AttCity;
        state.selectedPackingList.BillToState = location.AttState;
        state.selectedPackingList.BillToZIP = location.AttZIP;
        state.selectedPackingList.BillToPhone = location.AttPhone;
        state.selectedPackingList.BillToEmail = location.AttEmail;
      }
    },

    // Clear selected data
    clearSelectedPackingList: (state) => {
      state.selectedPackingList = null;
      state.selectedDetails = [];
      state.reportData = null;
      state.validationErrors = [];
      state.calculatedTotals = { totalQuantity: 0, totalValue: 0, itemCount: 0 };
    },

    clearSelectedDetails: (state) => {
      state.selectedDetails = [];
      state.editingDetail = null;
      state.selectedDetailId = null;
      state.calculatedTotals = { totalQuantity: 0, totalValue: 0, itemCount: 0 };
    },

    // Reset state
    resetPackingListState: (state) => {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setDropdownsLoading,
  setDetailsLoading,
  setReportLoading,
  setError,
  clearError,
  setValidationErrors,
  clearValidationErrors,

  fetchPackingListsStart,
  fetchPackingListsSuccess,
  fetchPackingListsFailure,

  fetchPackingListByIdStart,
  fetchPackingListByIdSuccess,
  fetchPackingListByIdFailure,

  createPackingListStart,
  createPackingListSuccess,
  createPackingListFailure,

  updatePackingListStart,
  updatePackingListSuccess,
  updatePackingListFailure,

  deletePackingListStart,
  deletePackingListSuccess,
  deletePackingListFailure,

  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,

  fetchDetailsStart,
  fetchDetailsSuccess,
  fetchDetailsFailure,

  addDetailStart,
  addDetailSuccess,
  addDetailFailure,

  updateDetailStart,
  updateDetailSuccess,
  updateDetailFailure,

  deleteDetailStart,
  deleteDetailSuccess,
  deleteDetailFailure,

  generateReportStart,
  generateReportSuccess,
  generateReportFailure,

  setSearchTerm,
  setActiveFilter,
  setPage,
  setPageSize,

  toggleDetailsPanel,
  setDetailsPanel,
  toggleShippingPanel,
  setShippingPanel,
  toggleExportPanel,
  setExportPanel,

  setEditingDetail,
  setSelectedDetailId,

  populateShippingFromLocation,

  clearSelectedPackingList,
  clearSelectedDetails,
  resetPackingListState,
} = packingListSlice.actions;

export default packingListSlice.reducer; 